import {
  Color3,
  MeshBuilder,
  Quaternion,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { RAPIER, type PhysicsWorld } from '@roadworld/physics';
import type { RgbColor } from '@roadworld/shared';
import type { Surface } from '../audio/TireSound.js';
import type { InputState } from '../input/Input.js';
import type { SurfaceKind } from '../world/surface.js';
import type { IVehicle, SurfaceLookup, VehicleKind } from './IVehicle.js';
import { MotorcycleTuning } from './motorcycleTuning.js';
import { applyInkOutline } from './outlines.js';
import type { SurfaceHandlingTable, TreeFellingConfig } from './tuning.js';

interface WheelState {
  steers: boolean;
  driven: boolean;
  visual: TransformNode;
}

/**
 * 2-wheel motorcycle. Structurally a sibling of [Car](./Car.ts) — same applyInput shape,
 * surface-aware grip model, CoM-impulse braking, tree-felling helpers — but with two
 * inline wheels (front steers + drives, rear drives) and a lighter chassis. Tuning lives
 * in [motorcycleTuning.ts](./motorcycleTuning.ts); the surface/grip math is identical
 * because we deliberately want the surface system to behave the same way for both
 * vehicles, only the *numbers* differing.
 *
 * Note: this class does not model lean. Babylon's left-handed scene + Rapier's
 * raycast-vehicle controller would need a custom lean joint or a chassis-tilt visual hack
 * to do that right; for an arcade prototype, fast small-angle steering captures enough
 * of the "bike feel" without the simulation tax.
 */
export class Motorcycle implements IVehicle {
  readonly kind: VehicleKind = 'motorcycle';

  private physics: PhysicsWorld;
  private body: RAPIER.RigidBody;
  private collider: RAPIER.Collider;
  private controller: RAPIER.DynamicRayCastVehicleController;
  private wheels: WheelState[] = [];
  private root: TransformNode;
  private bodyMesh: TransformNode;
  private currentSteer = 0;
  private brakeSmoothed = 0;
  private spawnPos: Vector3;
  private spawnYaw: number;
  private enabled = true;

  treeFelling: TreeFellingConfig | null = null;
  surfaceLookup: SurfaceLookup | null = null;
  surfaceHandling: SurfaceHandlingTable | null = null;

  private prevLinvelX = 0;
  private prevLinvelY = 0;
  private prevLinvelZ = 0;
  private prevHSpeed = 0;

  private appliedLinearDamping: number;
  private appliedAngularDamping: number;
  private appliedFrontSlip: number;
  private appliedFrontSide: number;
  private appliedRearSlip: number;
  private appliedRearSide: number;
  private gripScore = 1.0;
  private smoothedAccelG = 0;
  private smoothedSteerRate = 0;
  private prevForwardSpeedForGrip = 0;
  private prevSteerForGrip = 0;

  constructor(physics: PhysicsWorld, scene: Scene, spawnPos: Vector3, spawnYaw = 0) {
    this.physics = physics;
    this.spawnPos = spawnPos.clone();
    this.spawnYaw = spawnYaw;

    const T = MotorcycleTuning;

    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(spawnPos.x, spawnPos.y, spawnPos.z)
      .setRotation(yawToQuat(spawnYaw))
      .setCcdEnabled(true)
      .setLinearDamping(T.linearDamping)
      .setAngularDamping(T.angularDamping);
    this.body = physics.world.createRigidBody(bodyDesc);
    this.appliedLinearDamping = T.linearDamping;
    this.appliedAngularDamping = T.angularDamping;
    this.appliedFrontSlip = T.frictionSlip;
    this.appliedFrontSide = T.sideFrictionStiffness;
    this.appliedRearSlip = T.frictionSlip;
    this.appliedRearSide = T.sideFrictionStiffness;

    const colliderDesc = RAPIER.ColliderDesc.cuboid(
      T.chassisWidth * 0.5,
      T.chassisHeight * 0.5,
      T.chassisLength * 0.5,
    )
      .setDensity(0)
      .setFriction(0.4)
      .setRestitution(0.0);
    this.collider = physics.world.createCollider(colliderDesc, this.body);

    const w = T.chassisWidth;
    const h = T.chassisHeight;
    const d = T.chassisLength;
    const inertiaX = (T.chassisMass / 12) * (h * h + d * d);
    const inertiaY = (T.chassisMass / 12) * (w * w + d * d);
    const inertiaZ = (T.chassisMass / 12) * (w * w + h * h);
    this.body.setAdditionalMassProperties(
      T.chassisMass,
      { x: 0, y: T.centerOfMassY, z: 0 },
      { x: inertiaX, y: inertiaY, z: inertiaZ },
      { x: 0, y: 0, z: 0, w: 1 },
      true,
    );

    this.controller = physics.world.createVehicleController(this.body);
    const suspensionDir = { x: 0, y: -1, z: 0 };
    const wheelAxle = { x: -1, y: 0, z: 0 };

    // Two inline wheels — front steers + drives, rear drives only.
    const corners: Array<{ x: number; z: number; steers: boolean; driven: boolean }> = [
      { x: 0, z: T.frontAxle, steers: true, driven: true },
      { x: 0, z: -T.rearAxle, steers: false, driven: true },
    ];

    this.root = new TransformNode('motorcycle-root', scene);
    this.bodyMesh = createBikeMesh(scene, T);
    this.bodyMesh.parent = this.root;

    for (let i = 0; i < corners.length; i++) {
      const c = corners[i]!;
      this.controller.addWheel(
        { x: c.x, y: T.wheelAnchorY, z: c.z },
        suspensionDir,
        wheelAxle,
        T.suspensionRestLength,
        T.wheelRadius,
      );
      this.controller.setWheelSuspensionStiffness(i, T.suspensionStiffness);
      this.controller.setWheelMaxSuspensionTravel(i, T.suspensionTravel);
      this.controller.setWheelMaxSuspensionForce(i, T.maxSuspensionForce);
      this.controller.setWheelSuspensionCompression(i, T.suspensionCompression);
      this.controller.setWheelSuspensionRelaxation(i, T.suspensionRelaxation);
      this.controller.setWheelFrictionSlip(i, T.frictionSlip);
      this.controller.setWheelSideFrictionStiffness(i, T.sideFrictionStiffness);

      const wheelVisual = createBikeWheelMesh(scene, T, `bike-wheel-${i}`);
      wheelVisual.parent = this.root;
      this.wheels.push({ steers: c.steers, driven: c.driven, visual: wheelVisual });
    }
  }

  applyInput(input: InputState, dt: number): void {
    const T = MotorcycleTuning;
    const linvel = this.body.linvel();
    const speed = Math.hypot(linvel.x, linvel.y, linvel.z);

    const pos = this.body.translation();
    const surfaceKind: SurfaceKind = this.surfaceLookup?.(pos.x, pos.z) ?? 'road';
    const surfaceTable = this.surfaceHandling ?? (T.surfaceHandling as SurfaceHandlingTable);
    const sCfg = surfaceTable[surfaceKind];
    const targetDamping = T.linearDamping + sCfg.extraLinearDamping;
    if (targetDamping !== this.appliedLinearDamping) {
      this.body.setLinearDamping(targetDamping);
      this.appliedLinearDamping = targetDamping;
    }
    const targetAngDamping = T.angularDamping + sCfg.extraAngularDamping;
    if (targetAngDamping !== this.appliedAngularDamping) {
      this.body.setAngularDamping(targetAngDamping);
      this.appliedAngularDamping = targetAngDamping;
    }

    const speedRatio = Math.min(1, speed / T.steerSpeedReference);
    const maxSteer = T.maxSteer + (T.minSteerAtHighSpeed - T.maxSteer) * speedRatio;
    const targetSteer = input.steer * maxSteer;
    const ramp = 1 - Math.exp(-T.steerRate * dt);
    this.currentSteer += (targetSteer - this.currentSteer) * ramp;

    const horizSpeed = Math.hypot(linvel.x, linvel.z);
    const forwardSpeed = this.getForwardSpeed();

    // Dynamic grip — same model as the car.
    const G = T.gripStress;
    const smoothAlpha = 1 - Math.exp(-dt / G.derivativeSmoothing);
    const speedF = Math.min(1, speed / G.speedFull);
    const speedGate = Math.pow(speedF, G.speedExponent);
    const av = this.body.angvel();
    const lateralG = speed * Math.abs(av.y);
    const lateralGF = Math.min(1, lateralG / G.lateralGFull);
    const rawAccelG = Math.abs(forwardSpeed - this.prevForwardSpeedForGrip) / Math.max(dt, 1e-4);
    this.smoothedAccelG += (rawAccelG - this.smoothedAccelG) * smoothAlpha;
    const accelGF = Math.min(1, this.smoothedAccelG / G.accelGFull);
    const rawSteerRate = Math.abs(this.currentSteer - this.prevSteerForGrip) / Math.max(dt, 1e-4);
    this.smoothedSteerRate += (rawSteerRate - this.smoothedSteerRate) * smoothAlpha;
    const steerRateF = Math.min(1, this.smoothedSteerRate / G.steerRateFull);

    const otherFactors = (lateralGF + accelGF + steerRateF) / 3;
    const rawStress = otherFactors * speedGate;
    const stress = Math.min(1, rawStress * sCfg.gripStressMul);
    const targetGrip = 1 - stress;
    const delta = targetGrip - this.gripScore;
    const stepRate = delta < 0 ? T.gripLossRate : T.gripRecoverRate;
    const maxStep = stepRate * dt;
    this.gripScore += delta < -maxStep ? -maxStep : delta > maxStep ? maxStep : delta;

    const effectiveRearMul =
      sCfg.surfaceGripFloor + (1 - sCfg.surfaceGripFloor) * this.gripScore;

    const targetFrontSlip = T.frictionSlip;
    const targetFrontSide = T.sideFrictionStiffness;
    const targetRearSlip = T.frictionSlip * effectiveRearMul;
    const targetRearSide = T.sideFrictionStiffness * effectiveRearMul;
    const frontChanged =
      targetFrontSlip !== this.appliedFrontSlip || targetFrontSide !== this.appliedFrontSide;
    for (let i = 0; i < this.wheels.length; i++) {
      if (this.wheels[i]!.steers) {
        if (frontChanged) {
          this.controller.setWheelFrictionSlip(i, targetFrontSlip);
          this.controller.setWheelSideFrictionStiffness(i, targetFrontSide);
        }
      } else {
        this.controller.setWheelFrictionSlip(i, targetRearSlip);
        this.controller.setWheelSideFrictionStiffness(i, targetRearSide);
      }
    }
    if (frontChanged) {
      this.appliedFrontSlip = targetFrontSlip;
      this.appliedFrontSide = targetFrontSide;
    }
    this.appliedRearSlip = targetRearSlip;
    this.appliedRearSide = targetRearSide;

    let engineForce = 0;
    if (input.throttle > 0) engineForce = input.throttle * T.engineForce;

    const brakeRamp = 1 - Math.exp(-T.brakeRampRate * dt);
    this.brakeSmoothed += (input.brake - this.brakeSmoothed) * brakeRamp;

    const brakeWeight = clamp01(forwardSpeed / T.brakeReverseBlendSpeed);
    const reverseWeight = 1 - brakeWeight;
    if (input.brake > 0) {
      engineForce += -input.brake * T.engineForce * 0.7 * reverseWeight;
    }

    if (this.brakeSmoothed > 0.01 && horizSpeed > 0.2) {
      const decelForce = this.brakeSmoothed * T.brakeForce * brakeWeight;
      const impulseMag = decelForce * dt;
      const invSpeed = 1 / horizSpeed;
      const ix = -linvel.x * invSpeed * impulseMag;
      const iz = -linvel.z * invSpeed * impulseMag;
      this.body.applyImpulse({ x: ix, y: 0, z: iz }, true);
    }

    engineForce *= sCfg.throttleMul;

    const handbrake = input.handbrake ? T.handbrakeForce : 0;

    for (let i = 0; i < this.wheels.length; i++) {
      const w = this.wheels[i]!;
      this.controller.setWheelSteering(i, w.steers ? this.currentSteer : 0);
      this.controller.setWheelEngineForce(i, w.driven ? engineForce : 0);
      const rear = !w.steers;
      this.controller.setWheelBrake(i, rear ? handbrake : 0);
    }

    const rot = this.body.rotation();
    const rightX = 1 - 2 * (rot.y * rot.y + rot.z * rot.z);
    const rightZ = 2 * (rot.x * rot.z - rot.y * rot.w);
    const linvelNow = this.body.linvel();
    const lateralVel = linvelNow.x * rightX + linvelNow.z * rightZ;
    if (lateralVel !== 0) {
      const sign = lateralVel < 0 ? -1 : 1;
      const dragMag =
        sign * lateralVel * lateralVel * T.slipDrag.coefficient * sCfg.slipDragMul * dt;
      this.body.applyImpulse({ x: -rightX * dragMag, y: 0, z: -rightZ * dragMag }, true);
    }

    const avCap = this.body.angvel();
    const cap = sCfg.maxYawRateRadS;
    if (avCap.y > cap) this.body.setAngvel({ x: avCap.x, y: cap, z: avCap.z }, true);
    else if (avCap.y < -cap) this.body.setAngvel({ x: avCap.x, y: -cap, z: avCap.z }, true);

    this.prevForwardSpeedForGrip = forwardSpeed;
    this.prevSteerForGrip = this.currentSteer;

    // --- Arcade upright-keeper ---
    // Two inline wheels = zero passive roll stability. Apply a PD torque each substep that
    // drives the chassis's local +Y back toward world +Y. See motorcycleTuning.upright doc
    // for why this is necessary (otherwise the bike tips, raycasts go sideways, and Rapier
    // eventually produces NaN translations that take out the whole world).
    //
    // Math: bodyUp (= q · (0,1,0)) is the local up direction in world coords. The cross
    // bodyUp × worldUp gives the rotation axis that would rotate bodyUp to worldUp; its
    // magnitude is sin(tilt angle), so it auto-saturates (no need to clamp). Damping is
    // applied to angvel.x / angvel.z so we don't fight yaw (steering).
    const qq = this.body.rotation();
    const qx = qq.x, qy = qq.y, qz = qq.z, qw = qq.w;
    const upX = 2 * (qx * qy - qw * qz);
    // upY not needed (it's the cosine of tilt — only used implicitly via the cross magnitude).
    const upZ = 2 * (qy * qz + qw * qx);
    const avNow = this.body.angvel();
    const KP = T.upright.stiffness;
    const KD = T.upright.damping;
    // cross(bodyUp, worldUp=(0,1,0)) = (-upZ, 0, upX). Multiply by KP for the spring term;
    // subtract KD * angvel for the damper. The result is a torque (N·m). To apply over one
    // substep, scale by dt and feed to applyTorqueImpulse.
    const torqueX = -upZ * KP - avNow.x * KD;
    const torqueZ = upX * KP - avNow.z * KD;
    this.body.applyTorqueImpulse({ x: torqueX * dt, y: 0, z: torqueZ * dt }, true);

    this.controller.updateVehicle(dt);
  }

  syncVisual(): void {
    const t = this.body.translation();
    const q = this.body.rotation();
    this.root.position.set(t.x, t.y, t.z);
    this.ensureQuat(this.root).set(q.x, q.y, q.z, q.w);

    for (let i = 0; i < this.wheels.length; i++) {
      const w = this.wheels[i]!;
      const cs = this.controller.wheelChassisConnectionPointCs(i);
      if (!cs) continue;
      const suspensionLen =
        this.controller.wheelSuspensionLength(i) ?? MotorcycleTuning.suspensionRestLength;
      const steering = this.controller.wheelSteering(i) ?? 0;
      const rotation = this.controller.wheelRotation(i) ?? 0;

      w.visual.position.set(cs.x, cs.y - suspensionLen, cs.z);
      const yaw = Quaternion.RotationAxis(new Vector3(0, 1, 0), steering);
      const pitch = Quaternion.RotationAxis(new Vector3(1, 0, 0), rotation);
      this.ensureQuat(w.visual).copyFrom(yaw.multiply(pitch));
    }
  }

  position(): Vector3 {
    const t = this.body.translation();
    return new Vector3(t.x, t.y, t.z);
  }

  forward(): Vector3 {
    const q = this.body.rotation();
    const qq = new Quaternion(q.x, q.y, q.z, q.w);
    const f = new Vector3(0, 0, 1);
    f.rotateByQuaternionToRef(qq, f);
    return f;
  }

  getYaw(): number {
    const f = this.forward();
    return Math.atan2(f.x, f.z);
  }

  speed(): number {
    const v = this.body.linvel();
    return Math.hypot(v.x, v.y, v.z);
  }

  getForwardSpeed(): number {
    const v = this.body.linvel();
    const f = this.forward();
    return v.x * f.x + v.y * f.y + v.z * f.z;
  }

  velocity(): Vector3 {
    const v = this.body.linvel();
    return new Vector3(v.x, v.y, v.z);
  }

  getChassisCollider(): RAPIER.Collider {
    return this.collider;
  }

  beginFrame(): void {
    const v = this.body.linvel();
    this.prevLinvelX = v.x;
    this.prevLinvelY = v.y;
    this.prevLinvelZ = v.z;
    this.prevHSpeed = Math.hypot(v.x, v.z);
  }

  preStepHSpeedMs(): number {
    return this.prevHSpeed;
  }

  preStepHDirection(): { x: number; z: number } {
    if (this.prevHSpeed < 1e-3) return { x: 0, z: 0 };
    return { x: this.prevLinvelX / this.prevHSpeed, z: this.prevLinvelZ / this.prevHSpeed };
  }

  setHorizontalVelocityMs(dirX: number, dirZ: number, speedMs: number): void {
    const v = this.body.linvel();
    this.body.setLinvel({ x: dirX * speedMs, y: v.y, z: dirZ * speedMs }, true);
  }

  applyImpactSpeedLossMs(deltaMs: number): void {
    if (deltaMs <= 0) return;
    const v = this.body.linvel();
    const hSpeed = Math.hypot(v.x, v.z);
    if (hSpeed < 1e-3) return;
    const newSpeed = Math.max(0, hSpeed - deltaMs);
    const scale = newSpeed / hSpeed;
    this.body.setLinvel({ x: v.x * scale, y: v.y, z: v.z * scale }, true);
  }

  effectiveTreeFelling(): TreeFellingConfig {
    return this.treeFelling ?? (MotorcycleTuning.treeFelling as TreeFellingConfig);
  }

  setSpawn(pos: Vector3, yaw = 0): void {
    this.spawnPos = pos.clone();
    this.spawnYaw = yaw;
  }

  respawn(): void {
    this.body.setTranslation({ x: this.spawnPos.x, y: this.spawnPos.y, z: this.spawnPos.z }, true);
    this.body.setRotation(yawToQuat(this.spawnYaw), true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }

  recoverTo(x: number, y: number, z: number): void {
    this.body.setTranslation({ x, y, z }, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }

  /** Hard-teleport to (pos, yaw) with zero velocities. Used by the swap path. */
  teleportTo(pos: Vector3, yaw: number): void {
    this.body.setTranslation({ x: pos.x, y: pos.y, z: pos.z }, true);
    this.body.setRotation(yawToQuat(yaw), true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    // Reset transient handling state so the just-activated vehicle doesn't carry stale
    // grip / steering smoothing from before it was last disabled.
    this.currentSteer = 0;
    this.brakeSmoothed = 0;
    this.gripScore = 1.0;
    this.smoothedAccelG = 0;
    this.smoothedSteerRate = 0;
    this.prevForwardSpeedForGrip = 0;
    this.prevSteerForGrip = 0;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    this.body.setEnabled(enabled);
    if (enabled) {
      this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    }
    this.root.setEnabled(enabled);
  }

  dispose(): void {
    this.root.dispose();
    // Order matters: the vehicle controller references the rigid body; remove the
    // controller first so the next physics.step() doesn't iterate an orphaned
    // controller pointing at a freed body (causes Rapier "recursive use" errors).
    this.physics.world.removeVehicleController(this.controller);
    this.physics.world.removeRigidBody(this.body);
  }

  // ---- IVehicle multiplayer / audio integration ----
  // The motorcycle is an arcade prototype — it doesn't yet model an engine RPM or
  // wall-proximity wind. The methods below stub the IVehicle contract so the audio
  // pipeline keeps working when the player swaps to the bike. Real implementations can
  // be added in a future tuning pass.

  colliderHandle(): number {
    return this.collider.handle;
  }

  /** Update the chassis body color in place — mirrors Car.setChassisColor so the
   *  multiplayer-assigned player color is consistent on either vehicle. */
  setChassisColor(color: RgbColor): void {
    const body = this.bodyMesh
      .getChildMeshes(false)
      .find((m) => m.name === 'bike-body' || m.name === 'chassis-body');
    if (!body || !body.material) return;
    const mat = body.material as StandardMaterial;
    mat.diffuseColor = new Color3(color.r, color.g, color.b);
  }

  tickRpm(): void {
    /* no engine model on the bike — leave the gauge at idle */
  }

  getRpmGauge(): number {
    // Map speed → a plausible needle position so the HUD readout doesn't read 0 km/h
    // worth of RPM forever. Pure cosmetic; engine sound on the bike isn't audible yet.
    const speedKmh = this.speed() * 3.6;
    return 1200 + Math.min(speedKmh, 200) * 25; // 1200 idle → ~6200 at 200 km/h
  }

  getEngineInput(): number {
    return this.brakeSmoothed > 0.5 ? 0 : 1; // placeholder — full load while not braking
  }

  getNearestSideSource(_maxDistance: number): {
    proximity: number;
    lateralBias: number;
    approachVelocity: number;
  } {
    return { proximity: 0, lateralBias: 0, approachVelocity: 0 };
  }

  getCurrentSurface(): Surface {
    if (!this.surfaceLookup) return 'road';
    const t = this.body.translation();
    return this.surfaceLookup(t.x, t.z) ?? 'road';
  }

  rotationQuat(): { x: number; y: number; z: number; w: number } {
    const q = this.body.rotation();
    return { x: q.x, y: q.y, z: q.z, w: q.w };
  }

  steerAngle(): number {
    return this.currentSteer;
  }

  private ensureQuat(node: TransformNode): Quaternion {
    if (!node.rotationQuaternion) node.rotationQuaternion = Quaternion.Identity();
    return node.rotationQuaternion;
  }
}

function yawToQuat(yaw: number): RAPIER.Rotation {
  const half = yaw * 0.5;
  return { x: 0, y: Math.sin(half), z: 0, w: Math.cos(half) };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function createBikeMesh(scene: Scene, T: typeof MotorcycleTuning): TransformNode {
  const root = new TransformNode('bike-visual', scene);

  // Lower frame box — sits between the wheels.
  const frame = MeshBuilder.CreateBox(
    'bike-frame',
    {
      width: T.chassisWidth,
      height: T.chassisHeight * 0.45,
      depth: T.chassisLength * 0.9,
    },
    scene,
  );
  frame.parent = root;
  frame.position.y = -T.chassisHeight * 0.15;
  const frameMat = new StandardMaterial('bike-frame-mat', scene);
  frameMat.diffuseColor = new Color3(0.08, 0.4, 0.5);
  frameMat.specularColor = new Color3(0.3, 0.3, 0.3);
  frame.material = frameMat;
  applyInkOutline(frame);

  // Tank + seat stack on top of the frame, slightly shorter than the frame.
  const tank = MeshBuilder.CreateBox(
    'bike-tank',
    {
      width: T.chassisWidth * 0.95,
      height: T.chassisHeight * 0.35,
      depth: T.chassisLength * 0.55,
    },
    scene,
  );
  tank.parent = root;
  tank.position.set(0, T.chassisHeight * 0.2, T.chassisLength * 0.05);
  const tankMat = new StandardMaterial('bike-tank-mat', scene);
  tankMat.diffuseColor = new Color3(0.12, 0.55, 0.65);
  tank.material = tankMat;
  applyInkOutline(tank);

  // Handlebar nub — tiny box up front so you can read the heading.
  const bar = MeshBuilder.CreateBox(
    'bike-bar',
    {
      width: T.chassisWidth * 1.6,
      height: T.chassisHeight * 0.08,
      depth: T.chassisHeight * 0.08,
    },
    scene,
  );
  bar.parent = root;
  bar.position.set(0, T.chassisHeight * 0.4, T.chassisLength * 0.32);
  const barMat = new StandardMaterial('bike-bar-mat', scene);
  barMat.diffuseColor = new Color3(0.05, 0.05, 0.05);
  bar.material = barMat;
  // Thinner outline so the slender handlebar doesn't look visually doubled.
  applyInkOutline(bar, 0.02);

  return root;
}

function createBikeWheelMesh(
  scene: Scene,
  T: typeof MotorcycleTuning,
  name: string,
): TransformNode {
  const cyl = MeshBuilder.CreateCylinder(
    name,
    {
      diameter: T.wheelRadius * 2,
      height: T.wheelWidth,
      tessellation: 18,
    },
    scene,
  );
  cyl.rotation.z = Math.PI / 2;
  cyl.bakeCurrentTransformIntoVertices();

  const mat = new StandardMaterial(`${name}-mat`, scene);
  mat.diffuseColor = new Color3(0.05, 0.05, 0.05);
  cyl.material = mat;
  applyInkOutline(cyl, 0.02);
  return cyl;
}
